import {
  BgColors,
  CustomImageProps,
  HeadingLevels,
  LinkProps,
} from "@/utils/types";
import { BlockWrapper } from "./BlockWrapper";
import type { IntrinsicElement } from "@/utils/jsx";
import { Portable } from "../utility/Portable";
import { sanitize } from "@/utils/sanitize";
import { isExternal } from "@/utils/isExternal";
import { ImageObject } from "../modules/ImageObject";

type LinkItem = {
  _key: string;
} & LinkProps;

export interface RiverProps {
  backgroundColor?: BgColors;
  className?: string;
  colorStarter?: boolean;
  componentId?: string;
  headlingLevel: HeadingLevels;
  items: {
    _key: string;
    heading: string;
    image: CustomImageProps;
    message: any[];
    link?: LinkProps;
  }[];
  links?: LinkItem[];
  primaryEnder?: boolean;
}

export const River = ({
  backgroundColor = "bg-white",
  className,
  colorStarter,
  componentId,
  headlingLevel,
  items,
  links,
  primaryEnder,
}: RiverProps) => {
  const lightMode =
    sanitize(backgroundColor) === "bg-white" ||
    sanitize(backgroundColor) === "pattern" ||
    sanitize(backgroundColor) === "waves" ||
    sanitize(backgroundColor) === "bg-secondary" ||
    sanitize(backgroundColor) === "bg-tertiary";

  const blockProps = {
    backgroundColor,
    colorStarter,
    componentId,
    primaryEnder,
  };

  return (
    <BlockWrapper
      {...blockProps}
      className={`river${className ? ` ${className}` : ""}`}
    >
      <div className="flex flex-col gap-[70px] contained">
        {items?.map((item, index) => {
          const HeadingTag = (
            index === 0 ? `h${headlingLevel}` : "p"
          ) as IntrinsicElement;
          const isEven = index % 2 === 0;
          return (
            <div
              key={item._key}
              className={`flex flex-col gap-5 md:gap-[70px] ${isEven ? "md:flex-row-reverse" : "md:flex-row"}`}
            >
              <div
                className={`rounded-[24px] md:rounded-card  overflow-hidden relative w-full md:w-1/2 h-[300px] md:h-auto border-4 ${sanitize(backgroundColor) === "bg-secondary" ? "bg-white" : "border-secondary"}${
                  sanitize(backgroundColor) === "pattern" ? " shadow-md" : ""
                }`}
              >
                {item.image && (
                  <ImageObject
                    {...item.image}
                    imageWidth={800}
                    className="object-cover absolute w-full h-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                  />
                )}
              </div>
              <div
                className={`w-full md:w-1/2 flex flex-col items-start justify-center gap-4${sanitize(backgroundColor) === "waves" ? " bg-white p-4 shadow-md rounded-2xl" : ""} md:py-8`}
              >
                <HeadingTag
                  className={`font-display text-[40px] ${index === 0 ? "md:text-[60px]" : "md:text-[35px]"} ${lightMode ? "text-secondaryDark" : "text-white"} leading-[1]`}
                >
                  {item.heading}
                </HeadingTag>
                <Portable
                  content={item.message}
                  className={`${lightMode ? "text-black" : "text-white font-bold"}${
                    sanitize(backgroundColor) === "pattern"
                      ? " bg-secondary p-4 shadow-md"
                      : ""
                  } leading-[1.8]`}
                />
                {item.link && (
                  <a
                    className={`button text-center ${lightMode ? "secondary dark" : "tertiary"}`}
                    href={item.link.href}
                    rel={
                      isExternal(item.link.href)
                        ? "noreferrer noopener"
                        : undefined
                    }
                    target={isExternal(item.link.href) ? "_blank" : undefined}
                  >
                    {item.link.copy}
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {links && links.length > 0 && (
        <div className="flex items-center justify-center mt-16 md:mt-32 gap-4 flex-wrap">
          {links.map((link, index) => {
            return (
              <a
                className={`button text-center ${lightMode ? "secondary dark" : "tertiary"}`}
                key={link._key + index}
                href={link.href}
                rel={isExternal(link.href) ? "noreferrer noopener" : undefined}
                target={isExternal(link.href) ? "_blank" : undefined}
              >
                {link.copy}
              </a>
            );
          })}
        </div>
      )}
    </BlockWrapper>
  );
};
